import React, { Fragment } from "react";
import styled from "styled-components";
import * as Time from "@sellout/utils/.dist/time";
import { Colors, Icons, Loader, LoaderSizes } from "@sellout/ui";
import { setToken } from "../utils/Auth";
import ScrollTable, {
  ScrollTableBody,
  ScrollTableBodyCell,
  ScrollTableBodyRow,
  ScrollTableHeader,
  ScrollTableHeaderCell,
  ScrollTableNoContent,
  ScrollTableSpace,
} from "./ScrollableTable";
import SET_USER_ORG_CONTEXT_ID from "@sellout/models/.dist/graphql/mutations/setUserOrgContextId.mutation";
import { useMutation } from "@apollo/react-hooks";
import TextButton, {
  TextButtonSizes,
} from "@sellout/ui/build/components/TextButton";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as AppActions from "../redux/actions/app.actions";
import { PaginationTypes } from "@sellout/models/.dist/interfaces/IPagination";
import { ModalTypes } from "./modal/Modal";
import { media } from "@sellout/ui/build/utils/MediaQuery";

const Container = styled.div`
  display: flex;
  height: fit-content;
  max-height: 100%;
  width: 100%;
  padding-bottom: 30px;
  ${media.mobile`
      padding-bottom: 10px;
    `};
`;

const NoContentHead = styled.div`
  font-weight: 600;
  font-size: 1.8rem;
  color: ${Colors.Grey1};
  margin-bottom: 5px;
`;

const NoContentBody = styled.div`
  font-weight: 500;
  font-size: 1.8rem;
  color: ${Colors.Grey3};
`;

const LoaderContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Colors.White};
  border-radius: 0px 0px 10px 10px;
  padding: 10px 0px;
`;

enum NoContentMessages {
  NoCustomers = "No Customers yet",
  NoOrganizations = "No organizations yet",
  Problem = "This is a problem...",
}

enum TableHeaderLabels {
  OrgId = "Org Id",
  Name = "Name",
  Email = "Email",
  OwnerEmail = "Owner Email",
  PhoneNumber = "Phone Number",
  Created = "Created",
  Organizations = "Organizations",
  Actions = "Actions",
}

enum OrganizationMessages {
  NoOrganizations = "No Organizations",
  UnknownName = "Unknown Name",
  UnknownUser = "Unknown User",
}

enum TextButtonLabels {
  Login = "Login",
  ViewSettings = "View settings",
  Edit = "Edit",
  Delete = "Delete",
}

enum MarginValues {
  SmallRight = "0px 5px 0px 0px",
}

const CommonFlexProps = {
  flex: "1",
};

type TableHeaderProps = {
};

const TableHeader: React.FC<TableHeaderProps> = () => (
  /** Render */
  <ScrollTableHeader>
    <ScrollTableHeaderCell {...CommonFlexProps}>
      { TableHeaderLabels.Name}
    </ScrollTableHeaderCell>
    <ScrollTableSpace />
    <ScrollTableHeaderCell {...CommonFlexProps}>
      {TableHeaderLabels.Email}
    </ScrollTableHeaderCell>
    <ScrollTableSpace />
    <ScrollTableHeaderCell {...CommonFlexProps}>
      { TableHeaderLabels.PhoneNumber}
    </ScrollTableHeaderCell>
    <ScrollTableSpace />
    <ScrollTableHeaderCell {...CommonFlexProps}>
      { TableHeaderLabels.Organizations}
    </ScrollTableHeaderCell>
    <ScrollTableSpace />
    <ScrollTableHeaderCell {...CommonFlexProps}>
      {TableHeaderLabels.Actions}
    </ScrollTableHeaderCell>
    <ScrollTableSpace />
  </ScrollTableHeader>
);

type TableRowsProps = {
  organizations: any;
  selloutWebFlowSiteId?: string;
  isCustomer?: boolean;
  refetch?: Function;
};

const TableRows: React.FC<TableRowsProps> = ({
  organizations,
  isCustomer,
  refetch,
}) => {
  /** Hooks */
  const dispatch = useDispatch();
  const setOrgId = (orgId: string) => dispatch(AppActions.setOrgId(orgId));
  const history = useHistory();
  
  const [setUserOrgContextId] = useMutation(SET_USER_ORG_CONTEXT_ID, {
    onCompleted(data) {
      const { token } = data.setUserOrgContextId;
      setToken(token);
      window.location.href = "/admin/dashboard";
    },
  });
  const handleCustomerUpdate = (customerDetails) => {
    const modalPropsCustomer = {
      email: customerDetails?.email,
      phoneNumber: customerDetails?.phoneNumber,
      userId: customerDetails?.userId,
      preffredLogin: true,
    };
    dispatch(
      AppActions.pushModal(ModalTypes.UpdateSuperAdminCustomer, {
        modalPropsCustomer,
        refetch,
      })
    );
  };
  const deleteOrganization = (organization) => {
    dispatch(
      AppActions.pushModal(ModalTypes.DeleteSuperAdminOrg, {
        organization,
      })
    );
  };
  /** Render */
  return (
    <>
      {organizations?.map((organization: any, index: number) => {
        let { _id, orgName, createdAt, isOrgDeletable } = organization;

        const email =
          organization?.user && organization?.user?.email
            ? organization.user.email
            : OrganizationMessages.UnknownUser;
        const {
          email: customerEmail,
          phoneNumber: customerPhoneNumber,
          firstName,
          lastName,
          organization: customerOrganization,
        } = organization;

        const organizationName =
          customerOrganization?.map((org: any) => org.orgName).join(", ") ||
          OrganizationMessages.NoOrganizations;
        orgName = orgName || OrganizationMessages.UnknownName;

        const fullName = `${firstName} ${lastName}`;
        return (
          <ScrollTableBodyRow key={index} height="40px">
            <ScrollTableBodyCell {...CommonFlexProps}>
              {!isCustomer ? _id : fullName}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
            <ScrollTableBodyCell {...CommonFlexProps}>
              {!isCustomer ? orgName : customerEmail}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
            <ScrollTableBodyCell {...CommonFlexProps}>
              {!isCustomer ? email : customerPhoneNumber}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
            <ScrollTableBodyCell {...CommonFlexProps} style={true}>
              {!isCustomer ? Time.format(createdAt) : organizationName}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
            <ScrollTableBodyCell {...CommonFlexProps}>
              {!isCustomer ? (
                <>
                  <TextButton
                    size={TextButtonSizes.Small}
                    children={TextButtonLabels.Login}
                    margin={MarginValues.SmallRight}
                    onClick={() => {
                      setUserOrgContextId({
                        variables: {
                          orgId: _id,
                        },
                      });
                    }}
                  />
                  <TextButton
                    size={TextButtonSizes.Small}
                    children={TextButtonLabels.ViewSettings}
                    margin={MarginValues.SmallRight}
                    onClick={() => {
                      setOrgId(_id);
                      history.push(
                        `/admin/dashboard/super/organizations/settings?orgId=${_id}`
                      );
                    }}
                  />
                  {isOrgDeletable ? (
                    <TextButton
                      size={TextButtonSizes.Small}
                      children={TextButtonLabels.Delete}
                      margin={MarginValues.SmallRight}
                      onClick={() => deleteOrganization(organization)}
                    />
                  ) : null}
                </>
              ) : (
                <TextButton
                  size={TextButtonSizes.Small}
                  children={TextButtonLabels.Edit}
                  icon={Icons.Edit}
                  margin={MarginValues.SmallRight}
                  onClick={() => handleCustomerUpdate(organization)}
                />
              )}
            </ScrollTableBodyCell>
            <ScrollTableSpace />
          </ScrollTableBodyRow>
        );
      })}
    </>
  );
};

type SuperAdminOrganizationsTableProps = {
  organizations: any;
  selloutWebFlowSiteId?: string;
  fetchMore?: Function;
  paginationType?: PaginationTypes;
  isCustomer?: boolean;
  refetch?: Function;
};

const SuperAdminCustomersList: React.FC<
  SuperAdminOrganizationsTableProps
> = ({
  organizations,
  selloutWebFlowSiteId,
  fetchMore,
  paginationType,
  isCustomer,
  refetch,
}) => {
  const scrollContainer = React.useRef<any>(null);
  const isCustomerScreen = organizations?.length <= 0 && isCustomer;
  const noContentHead = isCustomerScreen
    ? NoContentMessages.NoCustomers
    : NoContentMessages.NoOrganizations;
  const noContentBody = NoContentMessages.Problem;
  if (organizations?.length <= 0 || isCustomerScreen) {
    return (
      <Container>
        <ScrollTable>
          <TableHeader />
          <ScrollTableBody>
            <ScrollTableNoContent>
              <NoContentHead>{noContentHead}</NoContentHead>
              <NoContentBody>{noContentBody}</NoContentBody>
            </ScrollTableNoContent>
          </ScrollTableBody>
        </ScrollTable>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <ScrollTable
          fetchMore={fetchMore}
          paginationType={paginationType}
          updateQuery={(prev: any, { fetchMoreResult }: any) => {
            if (!fetchMoreResult) return prev;
            if (!isCustomer) {
              return {
                ...prev,
                organizations: [
                  ...(prev.organizations || []),
                  ...(fetchMoreResult.organizations || []),
                ],
              };
            } else {
              return {
                ...prev,
                allUserProfilesAdmin: [
                  ...(prev.allUserProfilesAdmin || []),
                  ...(fetchMoreResult.allUserProfilesAdmin || []),
                ],
              };
            }
          }}
          scrollContainer={scrollContainer}
          name="organizations"
        >
          {(loading: boolean) => {
            return (
              <Fragment>
                <TableHeader />
                <ScrollTableBody ref={scrollContainer}>
                  <TableRows
                    organizations={organizations}
                    selloutWebFlowSiteId={selloutWebFlowSiteId}
                    isCustomer={isCustomer}
                    refetch={refetch}
                  />
                  {loading && (
                    <LoaderContainer>
                      <Loader size={LoaderSizes.Large} color={Colors.Orange} />
                    </LoaderContainer>
                  )}
                </ScrollTableBody>
              </Fragment>
            );
          }}
        </ScrollTable>
      </Container>
    </>
  );
};

export default SuperAdminCustomersList;
